import { LayoutLoading } from 'components/LayoutLoading'
import { IS_MAIN_COMPANY } from 'config'
import { useEffect, useMemo, useState } from 'react'
import { getAdminConfig } from 'services'
import { ButtonGroup, PlainTable } from 'stories/components'

import { HmdaActionTakenConfiguration } from './ActionTaken'
import { HmdaConversionConfiguration } from './Conversion'
import { HmdaDefaultPropertiesConfiguration } from './DefaultProperties'
import { HmdaFilingInformationConfiguration } from './FilingInformation'

export interface IHMDAMapType {
  entity: 'Loan' | 'Borrower'
  field: string
  fieldTitle: string
  map?: Record<string, any> | string[]
}

const GeneralConfiguration = () => {
  const [isLoading, setLoading] = useState(true)
  const [link, setLink] = useState('')
  const [data, setData] = useState<Record<string, IHMDAMapType>>({})

  useEffect(() => {
    getAdminConfig('hmda')
      .then(({ link, config }) => {
        setLink(link)
        setData(config)
      })
      .finally(() => setLoading(false))
  }, [])

  const renderMapTable = (data: Record<string, any> | string[]) => {
    return (
      <div>
        <table>
          {Array.isArray(data)
            ? data.map((val) => (
                <tr>
                  <td className="border-b">{val}</td>
                </tr>
              ))
            : Object.keys(data).map((key) => (
                <tr>
                  <td className="border-b pr-2 border-r">{key}</td>
                  <td className="border-b pl-2">{data[key]}</td>
                </tr>
              ))}
        </table>
      </div>
    )
  }

  const tableData = useMemo(() => {
    return Object.keys(data).map((key, index) => {
      const { entity, fieldTitle, map } = data[key]
      return [
        index + 1,
        <a target="_blank" className="btn-link" href={`${link}#${key}`}>
          {key}
        </a>,
        `${entity} => ${fieldTitle}`,
        !map ? '=' : renderMapTable(map),
      ]
    })
  }, [data])

  return (
    <div className="relative">
      <LayoutLoading show={isLoading} />
      <PlainTable
        thClass="!px-4"
        tdClass="px-4 py-2 whitespace-nowrap"
        header={['No', 'CFPB field', 'Field', 'Data Mapping']}
        data={tableData}
      />
    </div>
  )
}

export const HMDAConfiguration = () => {
  const tabs = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = { general: 'General' }
    if (IS_MAIN_COMPANY) map.actionTaken = 'Action Taken'
    map.conversion = 'HMDA Conversion'
    map.defaultProperties = 'HMDA Default Properties'
    map.filingInformation = 'HMDA Filing Information'
    return map
  }, [])

  const [tab, setTab] = useState<string>('general')

  const renderContent = () => {
    switch (tab) {
      case 'actionTaken':
        return IS_MAIN_COMPANY ? <HmdaActionTakenConfiguration /> : <GeneralConfiguration />
      case 'conversion':
        return <HmdaConversionConfiguration />
      case 'defaultProperties':
        return <HmdaDefaultPropertiesConfiguration />
      case 'filingInformation':
        return <HmdaFilingInformationConfiguration />
      case 'general':
      default:
        return <GeneralConfiguration />
    }
  }

  return (
    <div>
      <div className="mb-4">
        <ButtonGroup title={tabs} value={tab} onChange={setTab} />
      </div>
      {renderContent()}
    </div>
  )
}
